import React from 'react';
import { Search, TrendingUp, Target, BarChart, Compass, CheckCircle2 } from 'lucide-react';
import './WebsiteDevelopment.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

export default function SEOOptimization() {
  const features = [
    {
      icon: <Target size={24} />,
      title: 'Keyword Strategy',
      text: 'Identifying high-value search terms that drive qualified traffic to your business.'
    },
    {
      icon: <TrendingUp size={24} />,
      title: 'Technical SEO',
      text: 'Optimizing site structure, speed, and metadata to ensure search engines can index you perfectly.'
    },
    {
      icon: <BarChart size={24} />,
      title: 'Analytics & Reporting',
      text: 'Detailed monthly reports tracking your rankings, traffic sources, and conversion growth.'
    }
  ];

  const faqItems = [
    {
      question: 'How long does it take to see SEO results?',
      answer: 'SEO is a long-term investment. Most clients see significant ranking improvements within 3 to 6 months of implementation.'
    },
    {
      question: 'Do you provide on-page or off-page SEO?',
      answer: 'We provide a comprehensive strategy covering technical on-page optimization, content strategy, and authority building.'
    }
  ];

  return (
    <article className="website-page">
      <Hero 
        eyebrow="SEO Optimization"
        title={<>Dominate the <span className="hero-accent">Search</span> Results.</>}
        description="Don't just build a website—be found. We implement data-driven SEO strategies that increase visibility and drive organic revenue."
        primaryAction={{ label: "Analyze My Site", link: "#strategy" }}
        stats={[
          { label: "Growth", value: "Avg. +120% Traffic" },
          { label: "Ranking", value: "Top 10 Focused" }
        ]}
      />

      <section className="section" id="strategy">
        <div className="section-heading">
          <span className="eyebrow">Strategy</span>
          <h2>Organic Growth Systems.</h2>
          <p>We move beyond simple keywords to build a content authority machine that helps your business stand out from the competition.</p>
        </div>
        <div className="features-grid">
          {features.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="info-row">
          <div className="info-copy">
            <h3>Technical Excellence</h3>
            <p>Search engines favor fast, well-structured websites. Our SEO strategy begins with a perfect technical foundation.</p>
            <ul className="check-list">
              <li><CheckCircle2 size={18} /> Core Web Vitals optimization</li>
              <li><CheckCircle2 size={18} /> Schema.org markup</li>
              <li><CheckCircle2 size={18} /> Sitemaps & Indexing</li>
            </ul>
          </div>
          <div className="info-media">
            <img src="/images/seo-optimization/seo.jpg" alt="SEO" />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-heading">
          <span className="eyebrow">Deep Dive</span>
          <h2>Visibility Questions.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection title="Stop losing customers to your competitors." />
      <Footer />
    </article>
  );
}
